// 'use strict';

import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { LookupTreeComponent } from '@components/model-data/lookup-tree/lookup-tree.component';
import * as GC from '@grapecity/spread-sheets';
import { getUnique, isEmpty, isNumber } from '@helpers/arrays';
import { backoff } from '@helpers/backoff';
import { compareKeys } from '@helpers/object-sort';
import { removeDuplicatesFromArray } from '@helpers/remove-duplicates-from-array';
import { sleep } from '@helpers/sleep';
import * as spreadHelper from '@helpers/spread/cell-styling';
import { resumeAll } from '@helpers/spread/resume-all';
import { selectColumn, selectRow } from '@helpers/spread/sheet-selection';
import { suspendAll } from '@helpers/spread/suspend-all';
import { capitalize, isPercentage, percentageToNumber } from '@helpers/strings';
import { LookupData, SpreadLookupInfo } from '@models/lookup-data';
import { LookupDataValidation } from '@models/lookup-data-validation';
import { LookupNode } from '@models/lookup-node';
import { LookupSetup, TableType } from '@models/lookup-setup';
import { ResultsetItem } from '@models/resultset-item';
import { Scenario } from '@models/scenario';
import { PermissionsService } from '@services/auth/permissions.service';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { TableSetupService } from '@services/lookup-tables/table-setup.service';
import { MenuService } from '@services/menu/menu.service';
import { ResultTabsService } from '@services/results/result-tabs.service';
import { ScenarioService } from '@services/scenario/scenario.service';
import { SimulationSetService } from '@services/simulation-set/simulation-set.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { Toast,ToastrService } from 'ngx-toastr';
import { NotificationsService } from '@services/notifications/notifications.service';
import { forkJoin, fromEvent, interval, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';
import { curateTables } from './merge-tables';
import { isValidLookupData, isValidValue } from './validate-table';
import { ToastLevel } from '@shared/toasterSetup';
// import { clipboardClearOnCut } from '@helpers/clipboard-events';
export const PADDING_COLUMNS = 3;

const SYSTEM_TABLES = ['balancesheet', 'commitdraw'];
const INITIAL_ROWS = 5;
const INITIAL_COLUMNS = 5;
const DEFAULT_RESULT_CELL_VALUE = 0;
const DEBOUNCE_TIME = 250; // (In ms.) Debounce time for service calls to store tracking info into DB
const LOOKUP_NODE_ALL_OBJECT = { name: 'ALL', value: null, id: null, children: [], updated: false, expanded: false };

// Clone an object
const clone = <T>(obj: T) => JSON.parse(JSON.stringify(obj)) as T;

@Component({
  selector: 'app-lookup-data',
  templateUrl: './lookup-data.component.html',
  styleUrls: ['./lookup-data.component.scss'],
})
export class LookupDataComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tree', { static: false }) tree: LookupTreeComponent;
  @ViewChild('spreadContainer', { static: false }) spreadContainer: ElementRef;

  private subscriptions: Subscription[] = [];
  private timeOut: NodeJS.Timeout = null;
  private scenarioAndGlobalParamsSubscription: Subscription = null;
  private lookupTablesSubscription: Subscription = null;
  private scenarioGlobalSwitchSubscription: Subscription = null;

  private intervals = [];

  saving = false;
  public isLoading = true;
  public isProcessing = false;

  sidenavVisible = true;

  // Spread
  private timeout: NodeJS.Timer = null;
  private toastTimeout: NodeJS.Timer = null;
  private timeoutUnsaved: NodeJS.Timer = null;
  private tableSetupTimeout: NodeJS.Timer = null;

  private sheet: GC.Spread.Sheets.Worksheet = null;
  private spread: GC.Spread.Sheets.Workbook;
  private spreadCommands = GC.Spread.Sheets.Commands;
  private contextMenuDataFull: GC.Spread.Sheets.ContextMenu.IMenuItemData[];
  private contextMenuDataNoInsert: GC.Spread.Sheets.ContextMenu.IMenuItemData[];
  spreadSheetData: Array<SpreadLookupInfo> = [];
  hostStyle = { width: '100%', height: '100%' };

  public scenarios: Scenario[] = [];
  public lookupTables: LookupSetup[] = [];
  public tableValidation: LookupDataValidation = new LookupDataValidation();
  public modifiedLookupTablesIds = [];
  public invalidLookupTablesIds = [];
  public numberTrackedItems = 0;
  public unsavedLookupTables: LookupData[] = [];

  private userRoleCanEdit;

  displayedTablesCategory = 0;
  displayedTablesScenario = 0;
  displayedTables = 0;

  // Bug Fix 1837
  scenarioMnemonicDatatype = '';
  scenarioMnemonicDimensions = {};
  // scenariosFormControl = new FormControl('', [Validators.required]);
  scenariosFormControl = new UntypedFormControl('');
  drawerOpened = true;

  // selectedScenario: Scenario = new Scenario();
  selectedScenarios: Scenario[] = [];
  selectedTreeItem: LookupNode = LOOKUP_NODE_ALL_OBJECT;
  structureChanged = false;
  oldValuesBeforePastingEvent: any[];

  public get showScenariosControl() {
    // return this.tableDataService.scenarioSwitchState === 'scenario' && !this.isEditingAssumptions;
    return this.scenarios.length > 0 && !this.isEditingAssumptions && this.tableDataService.scenarioSwitchState === 'scenario';
  }

  get isEditingAssumptions() {
    return this.menu.isEditingAssumptions;
  }

  get selectedScenariosNames() {
    const maxLength = 25;
    const names = this.selectedScenariosJoined;
    return names.length < maxLength ? names : `${names.substring(0, maxLength)}...`;
  }

  get selectedScenariosJoined() {
    return this.selectedScenarios.map(s => s.scenario_name).join(', ');
  }

  get selectedScenariosMnemonics() {
    const maxLength = 25;
    const mnemonics = this.selectedScenariosMnemonicsJoined;
    return mnemonics.length < maxLength ? mnemonics : `${mnemonics.substring(0, maxLength)}...`;
  }

  get selectedScenariosMnemonicsJoined() {
    return this.selectedScenarios.map(s => s.mnemonic).join(', ');
  }

  private activeCell: { row: number; col: number } = { row: 0, col: 0 };

  private tempStyle = {};
  /** Contains number of the last non-empty row in the spreadsheet */
  lastNonEmptyRow = 0;
  private ctrlShiftArrowPressed: 'up' | 'down' | 'left' | 'right' | 'none';
  scenarioListForFilter: Promise<Map<number, Scenario>>;

  private showToast(level:ToastLevel, title = '', message: string) {
    this.zone.run(() => {
      this.notifications.showToast(level, {title:title, message:message} as Toast);
    });
  }

  constructor(
    private scenarioService: ScenarioService,
    private tableSetupService: TableSetupService,
    public tableDataService: TableDataService,
    private zone: NgZone,
    private notifications: NotificationsService,
    private simService: SimulationSetService,
    private menu: MenuService,
    private resultTabs: ResultTabsService,
    private changeRef: ChangeDetectorRef,
    private trackingService: TrackingService,
    private permissions: PermissionsService
  ) {}

  ngOnInit() {
    this.userRoleCanEdit = this.permissions.getPermission('ModelDataLookuptables') === 'W';
  }

  ngOnDestroy() {
    // Avoid memory leaks cleaning subscriptions and intervals
    this.subscriptions.forEach(subs => subs.unsubscribe());
    this.intervals.forEach(int => clearInterval(int));

    this.scenarios = [];
    this.selectedScenarios = [];

    this.lookupTables = [];
    this.tableDataService.lookupTables = [];

    // Set to false the isEditingAssumptions Flag:
    if (this.isEditingAssumptions) {
      // ! ⚠ Note: Do not move this out of the if-statement as there are needed actions on the setter
      this.menu.isEditingAssumptions = false;
    }
  }

  ngAfterViewInit() {
    this.tableDataService.scenarioSwitchState = 'scenario';
    this.getScenariosAndLookupTables();
    this.launchListeners();
    this.changeRef.detectChanges();
  }

  private lookupTablesUpdatedCallback(tables: any[]) {
    if (tables.length) {
      this.lookupTables =
        this.tableDataService.scenarioSwitchState === 'scenario'
          ? this.tableDataService.scenarioTables
          : this.tableDataService.globalTables;

      setTimeout(() => {
        this.prepareData();
        this.tableValidation = new LookupDataValidation();
      });
    }
  }

  private launchListeners() {
    // SUBSCRIPTIONS
    // =============
    this.subscriptions = [
      // MouseDownEvent
      fromEvent(this.spreadContainer.nativeElement, 'mousedown').subscribe((e: MouseEvent) => this.onContextMenu(e)),

      // PollingService
      interval(30000).subscribe(
        () => this.lookupTableSetupHasChanged(),
        err => {}
      ),

      // "selectNode"-like Listener for selectedTreeItem$
      this.tree.selectedTreeItem$.subscribe(node => {
        if (node && this.sheet) {
          this.onTreeItemSelected(node);
        }
      }),
    ];
  }

  private applyEditAssumptionsFiltering(): void {
    const activeTabScenarioId = this.resultTabs.activeView.scenario;
    const targetScenarios = [this.scenarios.find(scen => scen.scenario_id === activeTabScenarioId)];

    // Filter by Scenario
    this.filterByScenarios(targetScenarios);
  }

  private filterByScenarios(scenarioList: Scenario[]) {
    if (this.sheet) {
      const scenarioIds = this.selectedScenarios.map(selScen => selScen.scenario_id);
      scenarioList.forEach((scen: Scenario) => {
        if (scen && scen.scenario_id && !scenarioIds.includes(scen.scenario_id)) {
          // this.zone.run(() => this.selectedScenarios.push(scen));
          this.selectedScenarios = [...this.selectedScenarios, scen];
        }
      });

      this.onScenarioSelected(this.selectedScenarios);
    }
  }

  workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
    this.setupWorkBookEventBindings();
  }

  private setupWorkBook() {
    // Set up spreadsheet
    this.sheet = this.spread.getActiveSheet();

    this.sheet.suspendPaint();
    this.sheet.suspendEvent();
    this.sheet.suspendCalcService();

    // =========================
    //   General Sheet Options
    // =========================
    this.sheet.name('Lookup Tables');
    this.sheet.setColumnCount(INITIAL_COLUMNS);
    this.sheet.setRowCount(INITIAL_ROWS);
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 60;

    setDefaultBookOptions(this.spread.options);
    setDefaultSheetOptions(this.sheet.options);
    this.sheet.selectionPolicy(GC.Spread.Sheets.SelectionPolicy.range);

    this.sheet.options.protectionOptions.allowResizeColumns = true;

    // Context Menu
    this.spread.options.allowCopyPasteExcelStyle = true;
    SpreadContextMenu.setCopyCutPaste(this.spread);

    if (!this.userRoleCanEdit) {
      this.lockSheet();
    }

    this.contextMenuDataFull = [...this.spread.contextMenu.menuData];
    this.contextMenuDataNoInsert = [...this.spread.contextMenu.menuData];

    this.sheet.resumeCalcService();
    this.sheet.resumeEvent();
    this.sheet.resumePaint();
  }

  private setContextMenuData(menuData: GC.Spread.Sheets.ContextMenu.IMenuItemData[]) {
    this.spread.contextMenu.menuData = [...menuData];
  }

  private structureCanBeEdited(table: SpreadLookupInfo) {
    const isCategory = this.selectedTreeItem.isCategory;
    const multipleMnemonics = new Set(this.spreadSheetData.map(t => t.mnemonic)).size > 1;
    const isBalanceAssumption = table.category.toLowerCase() === 'balancesheet';
    const isCommitDraw = table.category.toLowerCase() === 'commitdraw';
    const structureCanBeEdited =
      !multipleMnemonics && !isBalanceAssumption && !this.isEditingAssumptions && !isCommitDraw && !isCategory && this.userRoleCanEdit;

    return structureCanBeEdited;
  }

  private setupWorkBookEventBindings() {
    this.sheet.bind(GC.Spread.Sheets.Events.CellClick, this.selectAllCallback);
    this.sheet.bind(GC.Spread.Sheets.Events.CellDoubleClick, this.selectAllCallback);

    // On Drag Fill Block
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlock, (e, info) => {
      const { row, col, rowCount, colCount } = info.fillRange;
      for (let r = row; r < rowCount + row; r++) {
        for (let c = col; c < colCount + col; c++) {
          this.tempStyle[`${r}-${c}`] = this.sheet.getStyle(r, c);
        }
      }
    });

    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, async (e, info) => {
      const { row, col, rowCount, colCount } = info.fillRange;

      this.isProcessing = rowCount * colCount > 50;
      this.changeRef.detectChanges();

      await sleep(50);
      suspendAll(this.sheet);

      for (let r = row; r < rowCount + row; r++) {
        for (let c = col; c < colCount + col; c++) {
          if (this.tempStyle[`${r}-${c}`]) {
            this.sheet.setStyle(r, c, this.tempStyle[`${r}-${c}`]);
          }
          const val = this.sheet.getCell(r, c).value();
          this.onCellValueChanged(r, c, val);
        }
      }

      resumeAll(this.sheet);

      this.isProcessing = false;
      this.changeRef.detectChanges();
    });

    // On value changed
    this.sheet.bind(GC.Spread.Sheets.Events.ValueChanged, (e, info) => {
      try {
        this.activeCell = { row: this.sheet.getActiveRowIndex(), col: this.sheet.getActiveColumnIndex() };
        this.onCellValueChanged(info.row, info.col, info.newValue, info.oldValue);
        this.sheet.setActiveCell(this.activeCell.row, this.activeCell.col);
      } catch (err) {
        console.warn(err);
      }
    });

    // When ending editing a cell
    this.sheet.bind(GC.Spread.Sheets.Events.EditEnding, (e, info) => {
      const { row, col, editingText: newValue } = info;
      const endingCell = this.sheet.getCell(info.row, info.col);

      if (!endingCell || !endingCell.value()) {
        return;
      }

      const prevValue = endingCell.value().toString();

      // If cell value didn't change, we check if cell is a pivot, column header of row header,
      // and we remove the "toBeDeleted" flag if necessary
      if (newValue && prevValue === newValue) {
        const table = this.spreadSheetData.find(t => t.startRow <= row && row <= t.endRow && t.startCol <= col && col <= t.endCol);

        if (table) {
          if (row === table.startRow + 1 && col === 0) {
            if (table.toBeDeleted) {
              table.toBeDeleted = false;
              spreadHelper.drawTable(table, this.sheet, this.structureCanBeEdited(table), this.userRoleCanEdit);
            }
          } else if (row === table.startRow + 1) {
            const colIndex = col - 1;
            if (table.toBeDeletedCols.includes(colIndex)) {
              this.spreadSheetData.forEach(t => {
                t.toBeDeletedCols = t.toBeDeletedCols.filter(i => i !== colIndex);
                spreadHelper.drawTable(t, this.sheet, this.structureCanBeEdited(table), this.userRoleCanEdit);
              });
            }
          } else if (col === table.startCol) {
            const rowIndex = row - (table.startRow + 2);

            if (table.toBeDeletedRows.includes(rowIndex)) {
              this.spreadSheetData.forEach(t => {
                t.toBeDeletedRows = t.toBeDeletedRows.filter(i => i !== rowIndex);
                spreadHelper.drawTable(t, this.sheet, this.structureCanBeEdited(table), this.userRoleCanEdit);
              });
            }
          }
          // this.addUnsavedTable(this.spreadSheetData);
        }
      }
    });

    let previousStateOfLockedCells;
    // Allowing selection of locked cells while editing so user can click outside to end editing
    this.sheet.bind(GC.Spread.Sheets.Events.EditStarting, (e, info) => {
      // console.log(`Edit starting`);
      previousStateOfLockedCells = this.sheet.options.protectionOptions.allowSelectLockedCells;
      this.sheet.options.protectionOptions.allowSelectLockedCells = true;
    });
    this.sheet.bind(GC.Spread.Sheets.Events.EditEnded, (e, info) => {
      // console.log(`Edit Ended`);
      this.sheet.options.protectionOptions.allowSelectLockedCells = previousStateOfLockedCells;
    });

    this.sheet.bind(GC.Spread.Sheets.Events.ValidationError, (e, info) => {
      // console.log(e, info);
    });

    this.sheet.bind(GC.Spread.Sheets.Events.InvalidOperation, (e, info) => {
      // console.log(e, info);
    });
    this.sheet.bind(GC.Spread.Sheets.Events.SelectionChanging, (e, info) => {
      // console.log(e, info);
      // console.log(this.lastNonEmptyRow);
    });

    this.sheet.bind(GC.Spread.Sheets.Events.TopRowChanged, (e, info) => {
      // Bug Fix 2249 - This makes sure that we will not scroll the spreadsheet when we do Ctrl+Shift+Up
      if (this.ctrlShiftArrowPressed === 'up') {
        this.sheet.showCell(info.oldTopRow, 0, GC.Spread.Sheets.VerticalPosition.top, GC.Spread.Sheets.HorizontalPosition.left);
      } else if (this.ctrlShiftArrowPressed === 'down') {
        // This makes the same approach for Ctrl+shift+Down
        const { row, rowCount } = this.sheet.getSelections()[0];
        this.sheet.showCell(info.oldTopRow, 0, GC.Spread.Sheets.VerticalPosition.top, GC.Spread.Sheets.HorizontalPosition.left);

        // If we're attemtping to change the view because our selection was too high up, stay there instead
        if (this.sheet.getViewportBottomRow(1) < row + rowCount) {
          this.sheet.showCell(row + rowCount, 0, GC.Spread.Sheets.VerticalPosition.bottom, GC.Spread.Sheets.HorizontalPosition.left);
        }
      }
    });

    this.sheet.bind(GC.Spread.Sheets.Events.SelectionChanged, (e, info) => {
      let { colCount, rowCount, row } = info.newSelections[0];
      const { col } = info.newSelections[0];
      // The following makes sure that the selection cannot exceed the current visible table

      const selectedTable = this.spreadSheetData.find(table => {
        if (this.ctrlShiftArrowPressed === 'up') {
          return table.startCol <= col && col <= table.endCol && table.startRow <= rowCount - 1 - row && rowCount - 1 - row <= table.endRow;
        } else {
          return table.startCol <= col && col <= table.endCol && table.startRow <= row && row <= table.endRow;
        }
      });

      // This only happens if the keybind is pressed and the selection is within a table
      if (selectedTable && this.ctrlShiftArrowPressed !== 'none') {
        row = this.ctrlShiftArrowPressed === 'up' ? selectedTable.startRow + 1 : row;

        colCount = col + colCount - 1 > selectedTable.endCol ? selectedTable.endCol - col + 1 : colCount;
        rowCount = row + rowCount - 1 > selectedTable.endRow ? selectedTable.endRow - row + 1 : rowCount;
        this.sheet.suspendEvent();
        this.sheet.setSelection(row, col, rowCount, colCount);
        this.ctrlShiftArrowPressed = 'none';
        this.sheet.resumeEvent();
      }
    });

    // Trigger on entering cell
    this.sheet.bind(GC.Spread.Sheets.Events.EnterCell, (event, info) => {
      const { row, col } = info;

      // Find out which table was clicked, if any
      const foundTable = this.spreadSheetData.find(
        t =>
          (row === t.startRow && row === t.startCol) || // Yellow cell
          (row === t.endRow + 1 && col === t.startCol) || // 1st dimension watermark-prompt
          (row === t.startRow + 1 && col === t.endCol + 1) || // 2nd dimension watermark-prompt
          (t.startRow < row && row <= t.endRow && t.startCol <= col && col <= t.endCol) // Table "body"
      );

      if (foundTable) {
        if (this.structureCanBeEdited(foundTable)) {
          // const isBlueCell = this.spreadSheetData.find(t => row === t.startRow + 1 && col === t.startCol);
          const isOrangeRow = this.spreadSheetData.find(t => t.startRow + 1 === row && 0 < col && col <= t.endCol + 1);
          const isGreenColumn = this.spreadSheetData.find(t => {
            return col === 0 && t.startRow + 2 <= row && row <= t.endRow + 1;
          });

          // const isAxisCell = isOrangeRow || isGreenColumn || isBlueCell;
          const isAxisCell = isOrangeRow || isGreenColumn;

          const tableIndex = this.spreadSheetData.indexOf(foundTable);
          // console.log(
          //   `📱 Enter Cell (Table index ${tableIndex}, Cell ${row}, ${col})`,
          //   isBlueCell ? '💙' : isOrangeRow ? '🧡' : isGreenColumn ? '💚' : '🖤'
          // );
          // console.log(
          //   `📱 Enter Cell (Table index ${tableIndex}, Cell ${row}, ${col})`,
          //   isOrangeRow ? '🧡' : isGreenColumn ? '💚' : '🖤'
          // );

          if (isAxisCell) {
            if (isOrangeRow) {
              this.unlockPaste('Row UN-LOCKED 🧡');
            } else if (isGreenColumn) {
              if (tableIndex === 0) {
                this.unlockPaste('Column UN-LOCKED 💚');
              } else {
                this.lockPaste(`Column keeps LOCKED (not 1st table)`);
              }
            }
            // else if (isBlueCell) {
            //   this.unlockPaste('Table UN-LOCKED 💙');
            // }
          } else {
            this.lockPaste(`Page remains LOCKED since its not an Axis cell`);
          }
        } else {
          this.lockPaste(`Page remains LOCKED since its structure cannot be modified`);
        }
      } else {
        this.lockPaste(`Page LOCKED - no table was clicked`);
      }
    });

    // Trigger on pasting data
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (event, info) => {
      const { row, col, rowCount, colCount } = info.cellRange;

      this.isProcessing = rowCount * colCount > 50;
      this.changeRef.detectChanges();

      // Make a copy of the range array before the pasting event happens
      this.oldValuesBeforePastingEvent = info.sheet.getArray(row, col, rowCount, colCount);
    });

    // Trigger on pasted data
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, async (event, info) => {
      const { row, col, rowCount, colCount } = info.cellRange;

      await sleep(50);

      // console.time(`ClipboardPasted: ${rowCount * colCount} cells`);

      // If no data was found after 'Pasted' event, it was most probably Right-Click + Paste
      // Get the actual data from the spread itself
      const pastedCells = this.sheet.getArray(row, col, rowCount, colCount);
      this.onRangeValueChanged(row, col, rowCount, colCount, pastedCells);

      // ! Part of fix for Defect 4584
      this.cleanHistory();

      // console.timeEnd(`ClipboardPasted: ${rowCount * colCount} cells`);

      this.isProcessing = false;
      this.changeRef.detectChanges();
    });

    // COMMAND MANAGER related
    // -----------------------
    const commandManager = this.spread.commandManager();

    // DELETE
    commandManager.register('deleteCell', () => this.onDeleteKeyPressed());
    commandManager.setShortcutKey('deleteCell', GC.Spread.Commands.Key.del);

    // Ctrl + Shift + Up
    commandManager.register('ctrlShiftUp', (e, info) => {
      this.ctrlShiftArrowPressed = 'up';
    });
    // Ctrl + Shift + Down
    commandManager.register('ctrlShiftDown', (e, info) => {
      this.ctrlShiftArrowPressed = 'down';
    });
    // Ctrl + Shift + Left
    commandManager.register('ctrlShiftLeft', (e, info) => {
      this.ctrlShiftArrowPressed = 'left';
    });
    // Ctrl + Shift + Right
    commandManager.register('ctrlShiftRight', (e, info) => {
      this.ctrlShiftArrowPressed = 'right';
    });
    commandManager.setShortcutKey('ctrlShiftUp', GC.Spread.Commands.Key.up, true, true);
    commandManager.setShortcutKey('ctrlShiftDown', GC.Spread.Commands.Key.down, true, true);
    commandManager.setShortcutKey('ctrlShiftLeft', GC.Spread.Commands.Key.left, true, true);
    commandManager.setShortcutKey('ctrlShiftRight', GC.Spread.Commands.Key.right, true, true);

    // -------------------------------------------------------------------
    // ⌨ F2 key
    commandManager.register('f2KeyEdit', (e, info) => {
      this.sheet.startEdit(true);
    });
    commandManager.setShortcutKey('f2KeyEdit', 113);

    // -------------------------------------------------------------------
    // ⌨ Ctrl + A
    commandManager.register('selectAll', (e, info) => {
      this.sheet.setSelection(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount());
    });
    commandManager.setShortcutKey('selectAll', GC.Spread.Commands.Key.a, true);

    // -------------------------------------------------------------------
    // ⌨ Ctrl + Space => Select Column
    commandManager.register('selectColumn', (e, info) => {
      // console.log('Select Column');
      selectColumn(this.sheet.getActiveColumnIndex() + 1, this.sheet);
    });
    commandManager.setShortcutKey('selectColumn', GC.Spread.Commands.Key.space, true);

    // -------------------------------------------------------------------
    // ⌨ Shift + Space => Select Row
    commandManager.register('selectRow', (e, info) => {
      // console.log('Selected Row');
      selectRow(this.sheet.getActiveRowIndex() + 1, this.sheet);
    });
    commandManager.setShortcutKey('selectRow', GC.Spread.Commands.Key.space, false, true);
  }

  onContextMenu(event) {
    const x = event.layerX;
    const y = event.layerY;
    const target = this.spread.getActiveSheet().hitTest(x, y);

    if (target.col === 0) {
      this.spreadSheetData.forEach((table, index) => {
        const within = target.row >= table.startRow && target.row <= table.endRow;
        if (within) {
          const { category } = table;
          if (SYSTEM_TABLES.indexOf(category.toLowerCase()) !== -1) {
            this.setContextMenuData(this.contextMenuDataNoInsert);
          } else {
            this.setContextMenuData(this.contextMenuDataFull);
          }
        }
      });
    }
  }

  isTableModified(mnemonic: string) {
    if (!mnemonic || this.modifiedLookupTablesIds.length === 0) {
      return false;
    }
    return this.modifiedLookupTablesIds.find(t => t.mnemonic.toLowerCase() === mnemonic.toLowerCase()) ? true : false;
  }

  private cleanHistory() {
    // ! Timeout needed
    setTimeout(() => {
      if (this.spread.undoManager()['Ac'] && this.spread.undoManager()['Bc']) {
        // Clean the history from ignored Action types
        const ignoredActions = ['autoFitColumn'];

        // Undo cleaning
        this.spread.undoManager()['Ac'] = this.spread.undoManager()['Ac'].filter(historyItem => !ignoredActions.includes(historyItem.cmd));
        // Redo cleaning
        this.spread.undoManager()['Bc'] = this.spread.undoManager()['Bc'].filter(historyItem => !ignoredActions.includes(historyItem.cmd));
      }
    });
  }

  onDeleteKeyPressed() {
    const selections = this.sheet.getSelections();
    // console.log(`👾 Delete pressed on ${selections.length} selections.`, selections);
    this.sheet.suspendPaint();
    try {
      for (const selection of selections) {
        const { row, rowCount, col, colCount } = selection;
        for (let r = row; r < row + rowCount; r++) {
          for (let c = col; c < col + colCount; c++) {
            if (!this.sheet.getCell(r, c).locked() || !this.sheet.options.isProtected) {
              const oldValue = this.sheet.getCell(r, c).value();
              const multiple = r > row || c > col;
              this.onCellValueChanged(r, c, null, oldValue, multiple);
            }
          }
        }
      }
    } catch (error) {
      console.error(`Error deleting values\n`, error);
    } finally {
      this.sheet.resumePaint();
      this.sheet.repaint();
    }
  }
  /**
   * Disallow pasting data in sheet
   */
  private lockPaste(message: string = null) {
    this.sheet.options.isProtected = true;
    this.spread.options.allowExtendPasteRange = false;
  }
  /**
   * Allow pasting data in sheet
   */
  private unlockPaste(message: string = null) {
    this.sheet.options.isProtected = false || !this.userRoleCanEdit;
    this.spread.options.allowExtendPasteRange = true;
  }

  // Bug Fix 3159: This will allow selecting all cells even if the sheet is completely protected
  // by clicking the corner
  private selectAllCallback = (sender, args) => {
    if (args.sheetArea === GC.Spread.Sheets.SheetArea.colHeader) {
      // Not required, can be done something like below (note: untested code)
      // this.sheet.setSelection(-1, args.col, -1, 1);
    }

    if (args.sheetArea === GC.Spread.Sheets.SheetArea.rowHeader) {
      // Not required
    }

    if (args.sheetArea === GC.Spread.Sheets.SheetArea.corner) {
      this.sheet.setSelection(-1, -1, -1, -1);
    }
  };

  /**
   * Iterates a range of cells and performs the 'onCellValueChanged' callback on each cell.
   */
  private onRangeValueChanged(row: number, col: number, rowCount: number, colCount: number, pastedCells: any[]) {
    let pasteSuccessful = true;
    let rowStopped = -1;
    let colStopped = -1;

    try {
      const defaultValue = pastedCells.flat()[0];
      suspendAll(this.sheet);

      rows: for (let r = row; r < rowCount + row; r++) {
        for (let c = col; c < colCount + col; c++) {
          const rawVal =
            pastedCells[r - row] !== undefined && pastedCells[r - row][c - col] !== undefined
              ? pastedCells[r - row][c - col]
              : defaultValue;

          const newVal = isNumber(rawVal)
            ? Number(rawVal)
            : isNumber(rawVal.replace(/\,/g, ''))
            ? Number(rawVal.replace(/\,/g, ''))
            : isPercentage(rawVal)
            ? percentageToNumber(rawVal)
            : rawVal.replace(/\,/g, '');

          let oldVal;
          try {
            oldVal = this.oldValuesBeforePastingEvent[r - row][c - col];
          } catch {
            oldVal = null;
          }

          // Don't show multiple toasts for multiple invalid cells
          const multiple = r > row || c > col;

          if (pasteSuccessful) {
            pasteSuccessful = pasteSuccessful && this.onCellValueChanged(r, c, newVal, oldVal, multiple, false);
            if (!pasteSuccessful) {
              rowStopped = r;
              colStopped = c;
            }
          }
          if (!pasteSuccessful) {
            setTimeout(() => this.sheet.getCell(r, c).value(oldVal));
            break rows;
          }
        }
      }
      resumeAll(this.sheet);
    } catch (error) {
      console.warn(error);
    } finally {
      if (!pasteSuccessful) {
        setTimeout(() => {
          this.sheet.clearSelection();
          this.sheet.addSelection(rowStopped, colStopped, 1, 1);
          this.sheet.setActiveCell(rowStopped, colStopped);
        });
      }
      this.oldValuesBeforePastingEvent = null;
    }
  }

  // Bug Fix 1891 - A new parameter is added to tell the code we're expecting multiple calls
  // to this function (copy&paste, multiple deletes, typing too fast) so it won't call the save method
  // We will call the method at the end of the validation loop with isMultiple = false to then call the save method
  private onCellValueChanged(row, col, val = null, oldVal = null, isMultiple = false, repaint = true): boolean {
    if (repaint) {
      this.sheet.suspendEvent();
      this.sheet.suspendPaint();
      this.sheet.suspendCalcService();
    }

    // const multipleMnemonics = new Set(this.spreadSheetData.map(t => t.mnemonic)).size > 1;

    let isValidChange = true;
    let valueFound = false;
    const foundTables: SpreadLookupInfo[] = [];
    let withinResults = false;

    for (const currentTable of this.spreadSheetData) {
      const structureCanBeEdited = this.structureCanBeEdited(currentTable);

      currentTable.previousData = {};

      const withinX = col >= currentTable.startCol && col <= currentTable.endCol;
      const withinY = row >= currentTable.startRow && row <= currentTable.endRow;
      const within = withinX && withinY;

      // Changed cell inside this table, including Title cell and Columns / Row names
      if (within) {
        valueFound = true;

        const withinResultsX = row >= currentTable.startRow + 2 && row <= currentTable.endRow;
        const withinResultsY = col >= currentTable.startCol + 1 && col <= currentTable.endCol;
        withinResults = withinResultsX && withinResultsY;

        // Changed cell is inside this table's results (values)
        if (withinResults) {
          const rowIndex = row - (currentTable.startRow + 2);
          const colIndex = currentTable.startCol + col - 1;

          if (isEmpty(val)) {
            val = 0;
            setTimeout(() => this.sheet.getCell(row, col).value(val));
          }

          // Result invalid if it's not a regular number, number with %, or number with commas
          if (!isNumber(val) && !isPercentage(val)) {
            isValidChange = false;
            this.flashInvalidCell(currentTable, -1, val, oldVal, row, col, isMultiple);
          } else {
            currentTable.results[rowIndex][colIndex] = val;
            currentTable.previousData = { dimension: 'results', value: oldVal, index: [rowIndex, colIndex] };
            foundTables.push(currentTable);
          }
        } else if (
          structureCanBeEdited &&
          col === currentTable.startCol &&
          row >= currentTable.startRow + 2 &&
          row <= currentTable.startRow + 2 + currentTable.rows.length
        ) {
          // If changed cell is a Dimension 1 Header - row changed
          const rowIndex = row - (currentTable.startRow + 2);
          let letsPush = true;

          // Loop over this table's dimensions to apply the new Dimension 1 Header (Rows)
          this.spreadSheetData.forEach((tNewRow, i) => {
            const headerRow = rowIndex + tNewRow.startRow + 2;
            const otherRows: any[] = clone(tNewRow.rows);
            // const otherRows = tNewRow.rows;

            otherRows.splice(rowIndex, 1);

            const included: boolean =
              val !== null && tNewRow.references[0].match(/Text/i)
                ? otherRows
                    .filter(x => !tNewRow.toBeDeletedRows.includes(tNewRow.rows.indexOf(x)))
                    .map(v => v.toString().toLowerCase())
                    .includes(val.toString().toLowerCase())
                : val !== null && otherRows.filter(x => !tNewRow.toBeDeletedRows.includes(x)).includes(val);

            if (included) {
              if (letsPush && !isMultiple) {
                this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${tNewRow.dimMnemonics[0]}`);
              }
              spreadHelper.flashCell(this.sheet, this.sheet.getCell(headerRow, col), oldVal);
              isValidChange = false;
              letsPush = false;
            } else if (val === null) {
              setTimeout(() => this.sheet.getCell(headerRow, col).value(oldVal));
              this.spreadSheetData.forEach(t => {
                if (t.toBeDeletedRows.indexOf(rowIndex) === -1) {
                  t.toBeDeletedRows.push(rowIndex);
                }
              });

              spreadHelper.drawTable(tNewRow, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
              tNewRow.previousData = { dimension: 'rows', value: oldVal, index: [rowIndex] };
            } else {
              const validRow = isValidValue(tNewRow.references[0], val);

              if (validRow) {
                this.sheet.getCell(headerRow, col).value(val);
                this.spreadSheetData.forEach(t => {
                  if (t.toBeDeletedRows.includes(rowIndex)) {
                    t.toBeDeletedRows = t.toBeDeletedRows.filter(x => x !== rowIndex);
                  }
                });

                tNewRow.rows[rowIndex] = val;

                spreadHelper.drawTable(tNewRow, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
                tNewRow.previousData = { dimension: 'rows', value: oldVal, index: [rowIndex] };
              } else {
                isValidChange = false;
                this.flashInvalidCell(tNewRow, 0, val, oldVal, row, col, isMultiple);
                letsPush = false;
              }
            }
          });
          if (letsPush) {
            foundTables.push(...this.spreadSheetData);
          }
        } else if (
          structureCanBeEdited &&
          row === currentTable.startRow + 1 &&
          col >= currentTable.startCol + 1 &&
          col <= currentTable.startCol + currentTable.cols.length
        ) {
          // If changed cell is a Dimension 2 Header - col changed
          const colIndex = currentTable.startCol + col - 1;
          let letsPush = true;
          // Loop over this table's dimensions to apply the new Dimension 2 Header (Columns)
          this.spreadSheetData.forEach((tNewCol, j) => {
            const headerRow = tNewCol.startRow + 1;
            const otherColumns: any[] = clone(tNewCol.cols);
            otherColumns.splice(colIndex, 1);

            const included: boolean =
              val !== null && tNewCol.references[1].match(/Text/i)
                ? otherColumns
                    .filter(x => !tNewCol.toBeDeletedCols.includes(tNewCol.cols.indexOf(x)))
                    .map(v => v.toString().toLowerCase())
                    .includes(val.toString().toLowerCase())
                : val !== null && otherColumns.includes(val);

            if (included) {
              if (!isMultiple || j === 0) {
                this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${tNewCol.dimMnemonics[1]}`);
              }
              isValidChange = false;
              letsPush = false;
              // spreadHelper.flashCell(this.sheet, this.sheet.getCell(headerRow, col), oldVal);
              this.flashInvalidCell(tNewCol, 1, val, oldVal, headerRow, col, isMultiple);
            } else if (val === null) {
              setTimeout(() => this.sheet.getCell(headerRow, col).value(oldVal));

              this.spreadSheetData.forEach(t => {
                if (t.toBeDeletedCols.indexOf(colIndex) === -1) {
                  t.toBeDeletedCols.push(colIndex);
                }
              });
              spreadHelper.drawTable(tNewCol, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
              tNewCol.previousData = { dimension: 'cols', value: oldVal, index: [colIndex] };
            } else {
              const validRow = isValidValue(tNewCol.references[1], val);

              if (validRow) {
                this.sheet.getCell(headerRow, col).value(val);
                this.spreadSheetData.forEach(t => {
                  if (t.toBeDeletedCols.includes(colIndex)) {
                    t.toBeDeletedCols = t.toBeDeletedCols.filter(x => x !== colIndex);
                  }
                });
                tNewCol.cols[colIndex] = val;
                spreadHelper.drawTable(tNewCol, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
                tNewCol.previousData = { dimension: 'cols', value: oldVal, index: [colIndex] };
              } else {
                isValidChange = false;
                letsPush = false;
                this.flashInvalidCell(tNewCol, 1, val, oldVal, row, col, isMultiple);
              }
            }
          });
          if (letsPush) {
            foundTables.push(...this.spreadSheetData);
          }
        } else if (structureCanBeEdited && currentTable.references.length === 3) {
          //
          // Pivot / third dimension changed
          //

          currentTable.toBeDeleted = val === null;

          if (oldVal === null && val !== null) {
            currentTable.allThirdDimensions = this.spreadSheetData.filter(t => t.id === currentTable.id).map(x => x.thirdDimension);

            currentTable.previousData = { dimension: 'pivots', value: oldVal, index: [0] };

            const dim1 = currentTable.references[0];
            const dim2 = currentTable.references[1];
            const isRowNumeric = dim1.match(/numeric/gi);
            const isColNumeric = dim2.match(/numeric/gi);
            const newRowValue = isRowNumeric ? '1' : 'A';
            const newColValue = isColNumeric ? '1' : 'A';

            currentTable.thirdDimension = val;
            currentTable.rows = [newRowValue];
            currentTable.cols = [newColValue];
            currentTable.numberOfRows = 1;
            currentTable.numberOfColumns = 1;
            currentTable.endRow = currentTable.startRow + 1 + currentTable.numberOfRows;
            currentTable.endCol = currentTable.startCol + currentTable.numberOfColumns;

            this.sheet.getCell(currentTable.startRow + 2, 0).value(newRowValue);
            this.sheet.getCell(currentTable.startRow + 1, currentTable.startCol + 1).value(newColValue);

            // Adding new row with 0's
            const emptyRow = new Array(currentTable.numberOfColumns).fill(DEFAULT_RESULT_CELL_VALUE);
            currentTable.results.push(emptyRow);
            this.sheet.setArray(currentTable.endRow, currentTable.endCol, [emptyRow]);

            this.addNewThirdDimensionPrompt(currentTable);
            spreadHelper.drawTable(currentTable, this.sheet, structureCanBeEdited, this.userRoleCanEdit);

            // isValidPaste = true;
            foundTables.push(currentTable);
          } else if (oldVal !== null) {
            let letsPush = true;
            const filteredData = this.spreadSheetData.filter(tt => tt.id === currentTable.id);
            const headerRow = currentTable.startRow + 1;

            const included: boolean =
              val !== null && currentTable.references[2].match(/Text/i)
                ? currentTable.allThirdDimensions
                    .filter(v => v !== null)
                    .map(v => v.toLowerCase())
                    .includes(val.toString())
                : val !== null && currentTable.allThirdDimensions.includes(val);

            if (included) {
              isValidChange = false;
              letsPush = false;
              this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${currentTable.dimMnemonics[2]}`);
              spreadHelper.flashCell(this.sheet, this.sheet.getCell(headerRow, col), oldVal);
            } else {
              if (val !== null) {
                const validPivot = isValidValue(currentTable.references[2], val);
                if (validPivot) {
                  this.sheet.getCell(headerRow, col).value(val);
                  currentTable.thirdDimension = val;
                } else {
                  isValidChange = false;
                  letsPush = false;
                  this.flashInvalidCell(currentTable, 2, val, oldVal, row, col, isMultiple);
                }
              } else {
                setTimeout(() => this.sheet.getCell(headerRow, col).value(oldVal));
              }

              currentTable.previousData = { dimension: 'pivots', value: oldVal, index: [0] };

              const allThirdDimensions = filteredData
                .filter(t => !t.toBeDeleted)
                .map(x => x.thirdDimension)
                .filter(v => v !== null);
              filteredData.forEach(t => (t.allThirdDimensions = clone(allThirdDimensions)));
            }

            if (letsPush) {
              foundTables.push(currentTable);
            }
          }

          spreadHelper.drawTable(currentTable, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
        }
      } else if (
        structureCanBeEdited &&
        currentTable.references.length > 1 &&
        row === currentTable.startRow + 1 &&
        col === currentTable.startCol + currentTable.cols.length + 1
      ) {
        // New Column added, continuous to the last one

        valueFound = true;

        if (currentTable.cols.map(c => c.toString().toLowerCase()).includes(val.toString().toLowerCase())) {
          isValidChange = false;
          this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${currentTable.dimMnemonics[1]}`);
          spreadHelper.flashCell(this.sheet, this.sheet.getCell(row, col));
        } else {
          const validColumn = isValidValue(currentTable.references[1], val);

          if (validColumn) {
            // Add new column to the spreadsheet if needed
            if (col > this.sheet.getColumnCount() - 1 - PADDING_COLUMNS) {
              this.sheet.addColumns(this.sheet.getColumnCount(), 1);
            }

            // Loop over this table's dimensions to apply the new column
            const idTables = this.spreadSheetData.filter(tt => tt.id === currentTable.id);
            idTables.forEach(tNewCol => {
              const newRow = tNewCol.startRow + 1;
              this.addRowIfEmpty(tNewCol);

              if (tNewCol.references.length === 3) {
                this.addPivotIfEmpty(tNewCol);
              }

              if (newRow !== row) {
                this.sheet.getCell(newRow, col).value(val);
              }
              tNewCol.previousData = { dimension: 'cols', value: oldVal, index: [tNewCol.endCol] };
              tNewCol.cols.push(val);
              tNewCol.numberOfColumns = tNewCol.cols.length || 1;
              tNewCol.endCol += 1;

              // Adding new column with 0's
              tNewCol.results.forEach(resultRow => resultRow.push(DEFAULT_RESULT_CELL_VALUE));
              this.sheet.setArray(newRow + 1, col, new Array(tNewCol.rows.length).fill(DEFAULT_RESULT_CELL_VALUE));

              spreadHelper.drawTable(tNewCol, this.sheet, structureCanBeEdited, this.userRoleCanEdit);

              foundTables.push(tNewCol);
            });
          } else {
            isValidChange = false;
            this.flashInvalidCell(currentTable, 1, val, oldVal, row, col, isMultiple);
          }
        }
      } else if (structureCanBeEdited && col === currentTable.startCol && row === currentTable.startRow + currentTable.rows.length + 2) {
        // New Row added, continuous to the last one

        valueFound = true;
        let totalRowsToAdd = 0;

        if (currentTable.rows.map(r => r.toString().toLowerCase()).includes(val.toString().toLowerCase())) {
          isValidChange = false;
          this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${currentTable.dimMnemonics[0]}`);
          spreadHelper.flashCell(this.sheet, this.sheet.getCell(row, col));
        } else {
          const validRow = isValidValue(currentTable.references[0], val);

          if (validRow) {
            // Loop over this table's dimensions to apply the new row
            const idTables = this.spreadSheetData.filter(tt => tt.id === currentTable.id);

            idTables.forEach((tNewRow, index) => {
              tNewRow.previousData = {
                dimension: 'rows',
                value: oldVal,
                index: [tNewRow.endRow - tNewRow.startRow - 2],
              };
              tNewRow.startRow += totalRowsToAdd;
              tNewRow.endRow += totalRowsToAdd;

              tNewRow.rows.push(val);
              tNewRow.numberOfRows = tNewRow.rows.length || 1;

              const newRow = tNewRow.endRow + 1;
              tNewRow.endRow += 1;

              this.sheet.getCell(newRow, col).value(val);
              this.sheet.addRows(newRow + 1, 1);

              this.addMissingDimensions(currentTable);

              // Adding new row with 0's
              const emptyRow = new Array(tNewRow.numberOfColumns).fill(DEFAULT_RESULT_CELL_VALUE);
              tNewRow.results.push(emptyRow);
              this.sheet.setArray(newRow, col + 1, [emptyRow]);
              totalRowsToAdd++;
              spreadHelper.drawTable(tNewRow, this.sheet, structureCanBeEdited, this.userRoleCanEdit);
              foundTables.push(tNewRow);

              // Cleanup
              const range =
                index === idTables.length - 1
                  ? this.sheet.getRange(tNewRow.endRow + 1, 0, this.sheet.getRowCount() - tNewRow.endRow, 1)
                  : this.sheet.getRange(tNewRow.endRow + 1, 0, 2, 1);
              range.clear(GC.Spread.Sheets.StorageType.data);
            });

            // Adjusting selected cell after rows were inserted
            const rowIndex = this.sheet.getActiveRowIndex() - totalRowsToAdd;
            const colIndex = this.sheet.getActiveColumnIndex();

            this.sheet.setActiveCell(rowIndex + totalRowsToAdd + 1, colIndex);
          } else {
            isValidChange = false;
            this.flashInvalidCell(currentTable, 0, val, oldVal, row, col, isMultiple);
          }
        }
      }

      if (valueFound) {
        // Changed cell found in one of the tables, no need to keep looking for it
        break;
      }
    }

    // New Third Dimension added - new pivot
    const lastTable = this.spreadSheetData[this.spreadSheetData.length - 1];

    if (col === 0 && row === lastTable.endRow + 4) {
      // Copying previous table and replacing Third dimension and Results

      const newTable: SpreadLookupInfo = clone(lastTable);
      newTable.allThirdDimensions = this.spreadSheetData.filter(t => t.id === newTable.id).map(x => x.thirdDimension);

      const newPivot = isNumber(val) ? Number(val) : val;
      const included = isNumber(val)
        ? newTable.allThirdDimensions.includes(newPivot)
        : newTable.allThirdDimensions.map(pivot => pivot.toLowerCase()).includes(newPivot.toLowerCase());

      if (included) {
        isValidChange = false;
        this.showToast(ToastLevel.Warning, '', `${val} : You cannot enter a duplicated ${newTable.dimMnemonics[2]}`);
        spreadHelper.flashCell(this.sheet, this.sheet.getCell(row, col));
      } else {
        const validPivot = isValidValue(newTable.references[2], val);

        if (validPivot) {
          newTable.thirdDimension = newPivot;

          newTable.startRow = newTable.endRow;
          newTable.endRow = newTable.startRow + newTable.rows.length + 1;
          if (lastTable.results.length > 0) {
            newTable.results.forEach(r => {
              if (r) {
                r.fill(DEFAULT_RESULT_CELL_VALUE);
              }
            });
          } else {
            // console.log('🛑 Do not try to add a 3rd dimension without creating the first one yet 🛑');
          }

          const tableSetup = this.lookupTables.find(t => t.Mnemonic.toLowerCase() === newTable.mnemonic.toLowerCase());
          if (tableSetup) {
            this.spreadSheetData.push(newTable);
            const newTableData = this.createQuery(this.spreadSheetData, `valueChanged (new pivot: ${val})`);
            tableSetup.Data = newTableData;
          }

          this.onTreeItemOrScenarioSelected();

          valueFound = true;
          foundTables.push(newTable);

          // Scroll down to the last non-empty row
          this.sheet.showRow(this.sheet.getRowCount(), GC.Spread.Sheets.VerticalPosition.center);
        } else {
          isValidChange = false;
          this.flashInvalidCell(newTable, 2, val, oldVal, row, col, isMultiple);
        }
      }
    }

    if (valueFound && foundTables.length && isValidChange) {
      if (withinResults) {
        this.debounceFunction(() => this.addUnsavedTable(foundTables), 100);
      } else {
        this.addUnsavedTable(foundTables);
      }
    }

    if (repaint) {
      this.sheet.resumeCalcService();
      this.sheet.resumePaint();
      this.sheet.resumeEvent();
      this.sheet.repaint();
    }

    this.cleanHistory();

    return isValidChange;
  }

  /**
   *
   * @param table Table containing the cell
   * @param dimension
   *  * 0: Dimension 1
   *  * 1: Dimension 2
   *  * 2: Dimension 3
   *  * -1: Result
   */
  flashInvalidCell(
    table: SpreadLookupInfo,
    dimension: -1 | 0 | 1 | 2,
    value: any,
    oldValue: any = null,
    row: number,
    col: number,
    isMultiple: boolean
  ) {
    // Only show 1 toast for multiple invalid cells
    // if (!isMultiple) {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }
    this.toastTimeout = setTimeout(() => {
      const dataType =
        dimension === -1
          ? 'numeric'
          : table.references[dimension].match(/D\d_(\w+)/) && table.references[dimension].match(/D\d_(\w+)/)[1]
          ? table.references[dimension].match(/D\d_(\w+)/)[1].toLowerCase()
          : null;
      const dimMnemonic = table.dimMnemonics[dimension];
      const toastMsg = dataType
        ? dataType === 'text'
          ? `${value} : Cannot have special characters in a Text dimension (${dimMnemonic})`
          : `${value} : Wrong data type (${dimension === -1 ? 'Result' : dimMnemonic} should be ${dataType})`
        : `${value} : Wrong data type`;

      // console.log(`flashInvalidCell`, { dataType, dimMnemonic, toastMsg, isMultiple });

      this.showToast(ToastLevel.Warning, '', toastMsg);
    }, 50);
    // }
    spreadHelper.flashCell(this.sheet, this.sheet.getCell(row, col), oldValue);
  }

  addUnsavedTable(spreadTables: SpreadLookupInfo[]) {
    const mnemonicId = spreadTables[0].id;
    const references = spreadTables[0].references;
    const [dim1, dim2, dim3] = references;
    const curatedTables = curateTables(spreadTables, this.lookupTables);

    const newData = this.createQuery(curatedTables, `addUnsavedTable (spreadTables ${spreadTables.length} items)`);

    this.tableValidation = isValidLookupData(
      newData,
      this.spreadSheetData.filter(t => t.id === mnemonicId)
    );

    if (!this.tableValidation.isValidTable && this.tableValidation.rangeToBeDeleted) {
      const { startRow, startCol, endRow, endCol } = this.tableValidation.rangeToBeDeleted;
      spreadHelper.setRangeToBeDeletedBackground(this.sheet.getRange(startRow, startCol, endRow, endCol));
    }

    // Check here to keep previous values except for Result
    if (this.tableValidation.isValidTable) {
      this.invalidLookupTablesIds = this.invalidLookupTablesIds.filter(item => item !== mnemonicId);
    } else {
      this.invalidLookupTablesIds.push(mnemonicId);
    }

    this.unsavedLookupTables = this.lookupTables.find(lut => lut.Id === mnemonicId).Data;

    newData.forEach(nd => {
      const existingItem = this.unsavedLookupTables.find(t => {
        if (dim3) {
          return t[dim1] === nd[dim1] && t[dim2] === nd[dim2] && t[dim3] === nd[dim3];
        }
        if (dim2) {
          return t[dim1] === nd[dim1] && t[dim2] === nd[dim2];
        }
        return t[dim1] === nd[dim1];
      });
      if (existingItem) {
        existingItem.Result = nd.Result;
        existingItem.toBeDeleted = nd.toBeDeleted;
      } else {
        this.unsavedLookupTables.push(nd);
      }
    });

    const newModifiedTable = {
      id: mnemonicId,
      mnemonic: spreadTables[0].mnemonic,
    };
    this.modifiedLookupTablesIds.push(newModifiedTable);
    this.tree.updatedTables[newModifiedTable.mnemonic] = true;

    if (this.tableSetupTimeout) {
      clearTimeout(this.tableSetupTimeout);
      this.tableSetupTimeout = null;
    }
    this.tableSetupTimeout = setTimeout(() => this.lookupTableSetupHasChanged(), 50);

    this.saveTrackingInfo();
  }

  public getUnsavedTablesNames(): string {
    return this.modifiedLookupTablesIds
      .map(t => t.mnemonic)
      .filter(getUnique)
      .join(', ');
  }

  clearUnsavedTables() {
    this.numberTrackedItems = 0;
    this.unsavedLookupTables = [];
    this.modifiedLookupTablesIds = [];
    this.tableValidation = new LookupDataValidation();
  }

  public saveTables(table_id = null) {
    this.saving = true;
    this.activeCell = { row: this.sheet.getActiveRowIndex(), col: this.sheet.getActiveColumnIndex() };

    this.tableDataService
      .save(table_id)
      .toPromise()
      .then(() => {
        // Show success toast
        this.notifications.showToast(
          ToastLevel.Success,
          { title: 'Success', message: 'Lookup data has been saved successfully.' } as Toast
        );
      })
      .catch(err => {
        console.log(err);
        // Show error toast
        this.notifications.showToast(
          ToastLevel.Error,
          { title: 'Save Failed', message: 'Failed to save lookup data. Please try again.' } as Toast
        );
      })
      .finally(() => {
        this.tableDataService
          .fetchLookupTables()
          .toPromise()
          .then(tables => {
            this.lookupTablesUpdatedCallback(tables);
            this.trackingService.updateModelDataChangesFlags('lookupTables', false, `saveTables`);
          })
          .finally(() => {
            this.clearUnsavedTables();
            this.tree.updatedTables = {};
            this.saving = false;
            // Clean undo history for lookup tables on discard
            this.spread.undoManager().clear();
            this.cleanHistory();
          });
      });
  }

  discardAllChanges() {
    this.trackingService.updateModelDataChangesFlags('lookupTables', false, `discardAllChanges`);
    this.discardChanges(null);
  }

  discardChanges(tableId: number = null) {
    this.activeCell = { row: this.sheet.getActiveRowIndex(), col: this.sheet.getActiveColumnIndex() };

    this.subscriptions.push(
      this.tableDataService.discardLookupTracking(tableId).subscribe(data => {
        this.tableDataService
          .fetchLookupTables()
          .toPromise()
          .then(tables => {
            this.lookupTablesUpdatedCallback(tables);
            this.clearUnsavedTables();
            if (tableId) {
              this.invalidLookupTablesIds = this.invalidLookupTablesIds.filter(item => item !== tableId);

              const discardedTable = this.lookupTables.find(t => t.Id === tableId);

              if (discardedTable) {
                this.tree.updatedTables[discardedTable.Mnemonic] = false;
              }
            } else {
              this.invalidLookupTablesIds = [];

              for (const tableMnemonic in this.tree.updatedTables) {
                if (this.tree.updatedTables.hasOwnProperty(tableMnemonic)) {
                  this.tree.updatedTables[tableMnemonic] = false;
                }
              }
            }

            // Clean undo history for lookup tables on discard
            this.spread.undoManager().clear();
            this.cleanHistory();
          });
      })
    );
  }

  /**
   * @description Sends content of 'unsavedLookupTables' to 'tableDataService.updateLookupTracking'
   */
  private saveTrackingInfo() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    this.timeout = setTimeout(() => {
      this.zone.run(() => {
        this.tableDataService
          .updateLookupTracking(this.unsavedLookupTables)
          .toPromise()
          .then(data => {
            this.numberTrackedItems = data.length;
          })
          .catch(err => {
            console.warn(`Error updating lookup tracking`, err);
          });
      });
    }, DEBOUNCE_TIME);
  }

  private createQuery(unsavedData: SpreadLookupInfo[], source: string): LookupData[] {
    // console.log(`🔥 createQuery`, { unsavedData: clone(unsavedData), source });

    const queries: LookupData[] = [];

    unsavedData.forEach(t => {
      const { mnemonic, id, rows, cols, thirdDimension: pivotValue, references, results } = t;
      const tableData = this.lookupTables.find(lut => lut.Id === id).Data;
      const [rowName, colName, pivotName] = references;
      const numberOfDimensions = references.length;
      const { toBeDeleted, toBeDeletedRows, toBeDeletedCols } = t;

      // Task 1985 - When storing lookupdata as text, we also need to set the dimension numeric when saving
      const rowOrder = rowName.match(/Text/i) ? 'D1_Numeric' : null;
      let colOrder = null,
        pivotOrder = null;

      if (numberOfDimensions > 1) {
        colOrder = colName.match(/Text/i) ? 'D2_Numeric' : null;
        if (numberOfDimensions > 2) {
          pivotOrder = pivotName.match(/Text/i) ? 'D3_Numeric' : null;
        }
      }

      let pivots = [];
      if (pivotOrder && numberOfDimensions === 3) {
        pivots = tableData.map(d => d[pivotName]).filter(getUnique);
      }

      if (cols.length === 0) {
        cols.push(null);
      }
      if (t.rows.length === 0) {
        const emptyQueryElement = new LookupData(mnemonic, id);
        queries.push(emptyQueryElement);
      } else {
        rows.forEach((row, r) => {
          const rowValue = row;
          cols.forEach((col, c) => {
            let query: LookupData;

            let modifiedDataPoint: LookupData;
            if (numberOfDimensions === 3) {
              modifiedDataPoint = tableData.find(st => st[rowName] === row && st[colName] === col && st[pivotName] === pivotValue);
            } else if (numberOfDimensions === 2) {
              modifiedDataPoint = tableData.find(st => st[rowName] === row && st[colName] === col);
            } else {
              modifiedDataPoint = tableData.find(st => st[rowName] === row);
            }

            const newResult = results && results[r] && results[r][c] ? results[r][c] : 0;

            if (modifiedDataPoint) {
              query = clone(modifiedDataPoint);

              // If the whole table (3rd dim) is flagged to be deleted,
              // any of its datapoints is flagged too
              if (toBeDeleted) {
                query.toBeDeleted = true;
              } else {
                const rowIndex = t.rows.indexOf(query[rowName]);
                const colIndex = t.cols.indexOf(query[colName]);

                if (toBeDeletedRows.includes(rowIndex) || toBeDeletedCols.includes(colIndex)) {
                  query.toBeDeleted = true;
                } else {
                  delete query.toBeDeleted;
                }
              }

              query.Result = newResult;
            } else {
              query = new LookupData(mnemonic, id, newResult);
            }

            // For this new cell, search for each dimension's order if existing, or get next order available if not found
            if (!isEmpty(rowName)) {
              query[rowName] = rowValue;
              if (rowOrder && isEmpty(query[rowOrder])) {
                // Find row order for this value if already exists
                const order = [...tableData, ...queries].find(value => value[rowName] === rowValue);
                if (order) {
                  query[rowOrder] = order[rowOrder];
                } else {
                  query[rowOrder] = this.getNextOrderingIndex([...tableData, ...queries], rowOrder);
                }
              }
              if (!isEmpty(colName) && numberOfDimensions > 1) {
                query[colName] = col;

                if (colOrder && isEmpty(query[colOrder])) {
                  // Find column order for this value if already exists
                  const order = [...tableData, ...queries].find(value => value[colName] === col);
                  if (order) {
                    query[colOrder] = order[colOrder];
                  } else {
                    query[colOrder] = this.getNextOrderingIndex([...tableData, ...queries], colOrder);
                  }
                }
                if (!isEmpty(pivotName) && numberOfDimensions > 2) {
                  query[pivotName] = pivotValue;
                  if (pivotOrder && isEmpty(query[pivotOrder])) {
                    // Find pivot order for this value if already exists
                    const order = [...tableData, ...queries].find(value => value[pivotName] === pivotValue);
                    if (order) {
                      query[pivotOrder] = order[pivotOrder];
                    } else {
                      query[pivotOrder] = this.getNextOrderingIndex([...tableData, ...queries], pivotOrder);
                    }
                  }
                }
              }
            }

            if (
              (numberOfDimensions === 1 && row !== null) ||
              (numberOfDimensions === 2 && row !== null && col !== null) ||
              (numberOfDimensions === 3 && row !== null && col !== null && pivotValue !== null)
            ) {
              queries.push(query);
            }
          });
        });
      }
      if (queries.length === 0) {
        queries.push(new LookupData(mnemonic, id));
      }
    });

    return queries;
  }

  private getScenariosAndLookupTables() {
    this.isLoading = true;
    this.structureChanged = false;

    this.scenarioAndGlobalParamsSubscription = forkJoin(
      this.scenarioService.getScenarios().pipe(first()),
      this.tableSetupService.getLookupTableGlobalParams().pipe(first()),
      this.tableDataService.fetchLookupTables()
    )
      .pipe(debounceTime(200), backoff(3, 250))
      .subscribe(
        data => {
          this.scenarios = clone(data[0])
            .filter(scenario => scenario.record_status === 'A')
            .sort((a: Scenario, b: Scenario) => a.scenario_id - b.scenario_id);

          this.scenarioMnemonicDatatype = data[1]['FilterOptions']
            ? data[1]['FilterOptions'].filter(x => x.Name === 'SCENARIO')[0].Value
            : 'ScenarioMn';

          const isSimulationView = this.menu.selectedMenuItem && this.menu.selectedMenuItem.outlet === 'simulation-outlet';

          // Assumptions filter
          if (isSimulationView) {
            // Put the data in the results store
            this.simService.loadDataFromDB();

            // Filter data
            if (this.isEditingAssumptions) {
              // Apply Prefiltering by Scenario for Edit Assumptions
              this.applyEditAssumptionsFiltering();
            } else if (this.menu.selectedMenuItem.routerLink === 'simulation-assumptions') {
              // Apply Prefiltering by Scenario for Assumptions Tab accesed from "Create/Edit Simulation" button
              this.filterBySimulationSetState();
            }
          }

          this.lookupTablesUpdatedCallback(data[2]);
        },
        error => {
          this.isLoading = false;
        }
      );
    this.subscriptions.push(this.scenarioAndGlobalParamsSubscription);

    // on ScenarioVsGlobalSwitch Changed after load
    this.scenarioGlobalSwitchSubscription = this.tableDataService.scenarioSwitchState$
      .pipe(distinctUntilChanged())
      .subscribe((switchValue: TableType) => {
        this.lookupTables = switchValue === 'scenario' ? this.tableDataService.scenarioTables : this.tableDataService.globalTables;
        this.prepareData();
        this.tableValidation = new LookupDataValidation();
      });

    this.subscriptions.push(this.scenarioGlobalSwitchSubscription);
  }

  private filterBySimulationSetState() {
    // 1) Get the scenario Ids of the baselines and stresses used in Simulation Set
    const getUsedScenarioIds = () => {
      let usedScenarioIds: number[] = [];
      if (this.simService.simulationSetStateBeforeEmpty) {
        this.simService.simulationSetStateBeforeEmpty.forEach((item: ResultsetItem) => {
          let stressIdList: number[] = [];
          if (item.StressList && item.StressList.length > 0) {
            // Avoids duplicates
            stressIdList = item.StressList.map(stress => stress.Scenario_ID);
          }
          usedScenarioIds = [...usedScenarioIds, ...stressIdList, item.Scenario_ID];
        });
      }
      usedScenarioIds = <number[]>removeDuplicatesFromArray(usedScenarioIds);
      return usedScenarioIds;
    };

    // 2) Filter the scenario Dropdown
    this.scenarios = this.scenarios.filter((scenario: Scenario) => getUsedScenarioIds().includes(scenario.scenario_id));

    // 3) Select those scenarios
    this.filterByScenarios(this.scenarios);
  }

  /**
   * @description *
   *   * Check which tables were Updated (the ones coming from tracking service)
   *   * Attaching Mnemonic ID to each LookupData table, and removing the cell Id.
   *   * Sorting tables' data by D3, D1, D2.
   */
  private prepareData() {
    this.clearUnsavedTables();
    let numberOfUpdatedTables = 0;

    this.lookupTables.forEach(table => {
      table.Dimensions = table.DimensionList.length;

      // Track updated tables
      if (table.Update) {
        numberOfUpdatedTables += 1;

        this.modifiedLookupTablesIds.push({
          id: table.Id,
          mnemonic: table.Mnemonic,
        });
        this.unsavedLookupTables.push(...table.Data);
        this.tree.updatedTables[table.Mnemonic] = true;
      }

      // Get table dimensions ((e.g. ['Mnemonic', 'AccountNum', 'ScenarioMn'])
      table.dimensions = [];
      table.DimensionList.forEach(dimension => {
        table.dimensions.push(dimension.DimMnemonic);
      });

      // Get table dimension references (e.g. ['D1_Text', 'D2_Numeric', 'D3_Text'])
      table.references = [];
      // Bug 49287 happened because the Dimensions came out of order from DB. This sort make sure that even if that happens again, the frontend has a safeguard.
      table.DimensionList.sort((a,b) => a.DimensionNumber - b.DimensionNumber)
      table.DimensionList.forEach(dimension => {
        const dimensionNumber = dimension.DimensionNumber;
        const dataType = capitalize(dimension.DataType);
        const reference: any = `D${dimensionNumber}_${dataType}`;
        table.references.push(reference);
      });

      const [d1Reference, d2Reference, d3Reference] = table.references;

      // When storing lookupdata as text, we also need to set the dimension numeric when saving
      // Just in case we get a table with zero dimensions, don't want to break everything
      let rowOrder = null,
        colOrder = null,
        pivotOrder = null;
      if (table.Dimensions > 0) {
        rowOrder = d1Reference.match(/Text/i) ? 'D1_Numeric' : null;
        if (table.Dimensions > 1) {
          colOrder = d2Reference.match(/Text/i) ? 'D2_Numeric' : null;
          if (table.Dimensions > 2) {
            pivotOrder = d3Reference.match(/Text/i) ? 'D3_Numeric' : null;
          }
        }
    }
      // Iterate over Balance Assumptions tables and set their D3_Numeric to be the corresponding Scenario_id
      if (table.DataTableType === 'BALANCESHEET') {
        table.Data.forEach((dataElement, index) => {
          const scenMnemonic = dataElement[d3Reference];
          const scen = this.scenarios.find(s => s.mnemonic.toLowerCase() === scenMnemonic.toLowerCase());
          const scenIndex = scen ? scen.scenario_id : index;
          dataElement[pivotOrder] = scenIndex;
        });
      }

      // Sorting dimensions
      if (table.Dimensions === 3) {
        table.Data.sort(compareKeys([pivotOrder || d3Reference, rowOrder || d1Reference, colOrder || d2Reference]));
      } else if (table.Dimensions === 2) {
        table.Data.sort(compareKeys([rowOrder || d1Reference, colOrder || d2Reference]));
      } else {
        table.Data.sort(compareKeys([rowOrder || d1Reference]));
      }
      table.Data.forEach((dataItem, index) => {
        dataItem.Mnemonic_Id = table.Id;
        delete dataItem.Id;
      });
    });

    this.numberTrackedItems = numberOfUpdatedTables;
    if (this.numberTrackedItems > 0) {
      this.trackingService.updateModelDataChangesFlags('lookupTables', true, `trackeditems bigger than 0`);
    }
    this.displayedTablesCategory = this.lookupTables.length;

    this.onTreeItemOrScenarioSelected();
  }

  private getScenarioMnemonicDimensions() {
    clone(this.lookupTables).forEach(table => {
      const category = table.DataTableType.toLowerCase();
      if (!this.scenarioMnemonicDimensions[category]) {
        const scenDimensions = table.DimensionList.filter(
          dim => dim.DimMnemonic.toLowerCase() === this.scenarioMnemonicDatatype.toLowerCase()
        );
        if (scenDimensions.length) {
          const dimType = capitalize(scenDimensions[0].DataType);
          const dimNumber = scenDimensions[0].DimensionNumber;
          this.scenarioMnemonicDimensions[category] = `D${dimNumber}_${dimType}`;
        }
      }
    });
  }

  selectAllTreeItem() {
    this.onTreeItemSelected(LOOKUP_NODE_ALL_OBJECT);
  }

  deselectScenario() {
    this.selectedScenarios = [];
    this.onScenarioSelected();
  }

  public onTreeItemSelected(node: LookupNode) {
    this.selectedTreeItem = node;

    this.onTreeItemOrScenarioSelected();
  }

  public onScenarioSelected(scenarioList: Scenario[] = null) {
    this.selectedScenarios = scenarioList || [];
    // Update the dropdown
    this.scenariosFormControl.setValue(this.selectedScenarios, { emitEvent: true });

    // Update Lookup Tables Data
    this.onTreeItemOrScenarioSelected();
  }

  public onTreeItemOrScenarioSelected() {
    try {
      let tables: LookupSetup[] = clone(this.lookupTables);

      const category = this.selectedTreeItem.name;
      const mnemonic = this.selectedTreeItem.value;

      if (this.selectedTreeItem.id === null && mnemonic) {
        // Find Id for selected mnemonic
        const matchedTable = tables.find(table => table.Mnemonic.toLowerCase() === mnemonic.toLowerCase());
        if (matchedTable) {
          this.selectedTreeItem.id = matchedTable.Id;
        }
      }
      this.lockSheet();

      if (mnemonic) {
        tables = tables.filter(table => table.Mnemonic.toLowerCase() === mnemonic.toLowerCase());
        this.sheet.options.isProtected = false;
      } else if (category && category.toUpperCase() !== 'ALL') {
        tables = tables.filter(table => table.DataTableType.toLowerCase() === category.toLowerCase());
      }

      this.displayedTablesCategory = tables.length;
      // Filter by Selected Scenarios
      if (this.selectedScenarios.length > 0) {
        tables.forEach(table => {
          if (table.DimensionList) {
            const hasScenario = table.DimensionList.find(dim => dim.DimMnemonic.toLowerCase() === 'scenariomn');
            if (hasScenario) {
              const filteringKey = `D${hasScenario.DimensionNumber}_${capitalize(hasScenario.DataType)}`;
              const filteredData = table.Data.slice().filter(data => {
                const hash = this.selectedScenarios.map(s => s.mnemonic.toLowerCase());
                const needle = data[filteringKey].toLowerCase();
                return hash.includes(needle);
              });
              table.Data = filteredData;
            }
          }
        });
      }

      this.addAllTablesToSpreadsheet(tables);

      this.displayedTablesScenario = tables.length;
    } catch {}
  }

  private lockSheet() {
    this.sheet.getRange(-1, 0, -1, 0).locked(true);
    this.sheet.options.isProtected = true;
    this.sheet.options.protectionOptions.allowSelectLockedCells = true;
  }

  private addAllTablesToSpreadsheet(tables: LookupSetup[]) {
    this.isLoading = true;
    if (tables.length === 0) {
      return;
    }
    this.sheet.suspendPaint();
    this.sheet.suspendEvent();
    this.sheet.suspendCalcService();

    this.lastNonEmptyRow = 0;
    spreadHelper.clearSheetStyles(this.sheet, INITIAL_ROWS, INITIAL_COLUMNS);
    this.sheet.setArray(0, 0, new Array(INITIAL_COLUMNS + 3).fill(['']));

    const initialDraw = false;
    this.spreadSheetData = [];

    tables.forEach((table, tableIndex) => {
      let info: SpreadLookupInfo = null;
      // Add 1 and 2 Dimension tables
      if (table.Dimensions < 3) {
        info = this.addTableToSpreadsheet(table);
        this.spreadSheetData.push(info);
      } else if (table.Dimensions === 3) {
        // Add 3 Dimension tables
        const thirdDimensionReference = table.references[2];
        const thirdDimensionMnemonic = table.dimensions[2];

        // Get 3rd dimension titles
        const thirdDimensionTitles = table.Data.map(d => d[thirdDimensionReference])
          .map(d => (d = d === Number(d) ? Number(d) : d))
          .filter(getUnique);

        // For empty 3D tables to appear in spread sheet
        if (thirdDimensionTitles.length === 0) {
          thirdDimensionTitles.push(null);
        }

        // Getting results size
        let resultsSize = 0;
        thirdDimensionTitles.forEach(thirdDimension => {
          const dataSlice = table.Data.filter(row => row[thirdDimensionReference] === thirdDimension);
          if (dataSlice.length > resultsSize) {
            resultsSize = dataSlice.length;
          }
        });

        // Adding one 2D table for each 3rd dimension
        thirdDimensionTitles.forEach(thirdDimension => {
          const dataSlice = table.Data.filter(row => row[thirdDimensionReference] === thirdDimension);
          // Empty 3D tables won't show up after navigating back
          // All pivots for this mnemonic should have same-sized results
          if (dataSlice.length === resultsSize) {
            const tableSlice = { ...table, Data: dataSlice };
            info = this.addTableToSpreadsheet(tableSlice, thirdDimension);
            info.allThirdDimensions = thirdDimensionTitles;
            this.spreadSheetData.push(info);
          }
        });

        // Add watermark / placeholder for new third dimension
        if (this.structureCanBeEdited(info) && tables.length === 1 && thirdDimensionTitles.length > 0 && thirdDimensionTitles[0]) {
          this.addNewThirdDimensionPrompt(info);
        }
      }
      this.displayedTables = this.spreadSheetData.length;
    });

    // Draw all tables
    this.spreadSheetData.forEach(table => {
      spreadHelper.drawTable(table, this.sheet, this.structureCanBeEdited(table), this.userRoleCanEdit);
    });

    spreadHelper.autoFitColumn(this.sheet, 0);

    this.sheet.setActiveCell(this.activeCell.row, this.activeCell.col);
    this.menu.emitResize();

    this.sheet.resumeCalcService();
    this.sheet.resumeEvent();
    this.sheet.resumePaint();

    this.isLoading = false;
  }

  private addTableToSpreadsheet(table: LookupSetup, thirdDimensionTitle = null): SpreadLookupInfo {
    /**
     * Example for 2D table: 'RSFloors'
     *
     * ┌──────────┐
     * │ RSFloors │
     * ├──────────┼───────────┬────────┬─────────┐  ColumTitles: ["<default>", "SwapCV", "TreasCV"]
     * │          │ <default> │ SwapCV │ TreasCV │  RowTitles: [0, 3, 12, 36, 60, 120, 360]
     * ├──────────┼───────────┼────────┼─────────┤  colReference: "D2_Text"
     * │        0 │    0.0005 │ 0.0003 │  0.0001 │  rowReference: "D1_Numeric"
     * │        3 │    0.0015 │  0.001 │  0.0003 │  Table size: 3 cols x 7 rows (ColumTitles.length x RowTitles.length)
     * │       12 │    0.0025 │ 0.0015 │   0.001 │
     * │       36 │     0.005 │  0.003 │   0.002 │
     * │       60 │    0.0075 │  0.005 │  0.0035 │
     * │      120 │      0.01 │ 0.0085 │   0.007 │
     * │      360 │     0.015 │ 0.0125 │    0.01 │
     * └──────────┴───────────┴────────┴─────────┘
     *
     * TABLE:
     *   Data: (21) [{…}, {…},..., {…}]
     *   DataTableType: "Pricing"
     *   DimensionList: (2) [
     *     {TableMnemonic: "RSFloors", DimensionNumber: 1, DataType: "Numeric", Interpolation: "LINEAR",
     *      DimMnemonic: "CurveTerm", DimExtension: ""},
     *     {TableMnemonic: "RSFloors", DimensionNumber: 2, DataType: "Text", Interpolation: "EXACT",
     *      DimMnemonic: "Mnemonic", DimExtension: ""}
     *   ]
     *   Mnemonic: "RSFloors"
     *   Name: "Rate Shock Floors"
     *   ResultType: "Numeric"
     *
     *
     */

    const data = table.Data;

    const references = table.references;
    const dimMnemonics = table.dimensions;

    const d1Reference = references[0];
    const d2Reference = references[1] || null;
    const d3Reference = references[2] || null;
    const d1Mnemonic = dimMnemonics[0];
    const d2Mnemonic = dimMnemonics[1] || null;
    const d3Mnemonic = dimMnemonics[2] || null;

    let columnTitles = [];
    const startingRow = this.lastNonEmptyRow;
    const isBalanceAssumption = table.DataTableType.toLowerCase() === 'balancesheet';
    const isCommitDraw = table.DataTableType.toLowerCase() === 'commitdraw';
    const editLock = !this.selectedTreeItem.value && !this.userRoleCanEdit;
    const listOfThirdDimensions: any[] = [];

    // Fill Row Titles
    const rowTitles = data
      .map(d => d[d1Reference])
      .map(d => (d = d === Number(d) ? Number(d) : d))
      .filter(getUnique);

    const numberOfRows = rowTitles.length;
    let numberOfColumns = 0;
    this.lastNonEmptyRow += numberOfRows + 4;

    // Adding rows to spreadsheet so it have space for the new inserted table
    this.sheet.addRows(this.sheet.getRowCount(), numberOfRows + 4);

    this.sheet.setArray(startingRow + 2, 0, rowTitles);

    // Fill Column Titles
    if (references.length > 1) {
      columnTitles = data
        .map(d => d[d2Reference])
        .map(d => (d = d === Number(d) ? Number(d) : d))
        .filter(getUnique);
      numberOfColumns = columnTitles.length;
      // Add columns to spreadsheet if needed
      const totalColumnsNeeded = numberOfColumns + 1 + PADDING_COLUMNS; // Row headers + table columns + 2 padding rows
      if (this.sheet.getColumnCount() < totalColumnsNeeded) {
        this.sheet.addColumns(this.sheet.getColumnCount(), totalColumnsNeeded - this.sheet.getColumnCount());
      }
      this.sheet.setArray(startingRow + 1, 1, [columnTitles]);
    } else {
      numberOfColumns = 1;
    }

    // Fill results
    if (table.Dimensions === 1) {
      const values = data.map(d => d.Result);
      this.sheet.setArray(startingRow + 2, 1, values);
    } else {
      columnTitles.forEach((col, index) => {
        const values = data.filter(d => d[d2Reference] === col).map(d => d.Result);
        this.sheet.setArray(startingRow + 2, 1 + index, values);
      });
    }

    let toBeDeleted = false;
    const toBeDeletedRows = [];
    const toBeDeletedCols = [];

    toBeDeleted = table.Dimensions > 2 && table.Data.every(x => x.toBeDeleted);
    if (!toBeDeleted) {
      rowTitles.forEach((r, i) => {
        const toBeDeletedRow = table.Data.filter(d => d[d1Reference] === r);
        const allDeleted = toBeDeletedRow.every(x => x.toBeDeleted);

        if (toBeDeletedRow.length > 0 && allDeleted) {
          toBeDeletedRows.push(i);
        }
      });

      if (table.Dimensions > 1) {
        columnTitles.forEach((c, i) => {
          const toBeDeletedCol = table.Data.filter(d => d[d2Reference] === c);
          const allDeleted = toBeDeletedCol.every(x => x.toBeDeleted);
          if (toBeDeletedCol.length > 0 && allDeleted) {
            toBeDeletedCols.push(i);
          }
        });
      }
    }

    const mnemonicCell = this.sheet.getCell(startingRow, 0);
    const subtitleCell = this.sheet.getCell(startingRow, 1);
    const pivotCell = this.sheet.getCell(startingRow + 1, 0);
    mnemonicCell.locked(true);
    subtitleCell.locked(true);
    pivotCell.locked(true);

    const firstColumn = this.sheet.getRange(startingRow + 2, 0, numberOfRows, 1);
    const titleRow = this.sheet.getRange(startingRow + 1, 1, 1, columnTitles.length);
    const resultsRange = this.sheet.getRange(startingRow + 2, 1, numberOfRows, numberOfColumns);

    firstColumn.locked(isBalanceAssumption || isCommitDraw || editLock);
    titleRow.locked(isBalanceAssumption || table.Dimensions === 1 || editLock);
    resultsRange.locked(editLock);

    if (isBalanceAssumption || isCommitDraw) {
      spreadHelper.setImmutableStyle(firstColumn);
      spreadHelper.setImmutableStyle(titleRow);
      spreadHelper.setImmutableStyle(pivotCell);
    }

    this.sheet.options.isProtected = true;
    // Allows clicks if user cannot edit so they can copy values
    this.sheet.options.protectionOptions.allowSelectLockedCells = editLock || !this.userRoleCanEdit;

    // Returning Spread Info object
    const spreadInfo: SpreadLookupInfo = {
      startRow: startingRow,
      startCol: 0,
      endRow: startingRow + rowTitles.length + 1,
      endCol: 0 + numberOfColumns,
      mnemonic: table.Mnemonic,
      id: table.Id,
      rows: rowTitles,
      numberOfRows: rowTitles.length,
      cols: columnTitles,
      numberOfColumns,
      thirdDimension: thirdDimensionTitle,
      allThirdDimensions: listOfThirdDimensions,
      references,
      dimMnemonics,
      results: this.sheet.getArray(startingRow + 2, 1, rowTitles.length, numberOfColumns, false),
      category: table.DataTableType,
      toBeDeleted: toBeDeleted,
      toBeDeletedRows: clone(toBeDeletedRows),
      toBeDeletedCols: clone(toBeDeletedCols),
    };

    return spreadInfo;
  }

  /**
   *
   * @description Returns an array of 2 arrays containing
   * - result[0]: Array with 1, 2 or 3 values of the referenced columns in LookupData
   * - result[1]: Array with 1, 2 or 3 values of the referenced columns's DimMnemonics
   * @returns Example: `[['D1_Text', 'D2_Numeric', 'D3_Text'], ["Mnemonic", "PdNum", "ScenarioMn"]]`
   */
  getColumnReferences(table: LookupSetup) {
    const refs = [];
    const dims = [];

    table.DimensionList.forEach(dimension => {
      const dimensionNumber = dimension.DimensionNumber;
      const dataType = capitalize(dimension.DataType);
      const reference = `D${dimensionNumber}_${dataType}`;
      refs.push(reference);
      dims.push(dimension.DimMnemonic);
    });
    return [refs, dims];
  }

  private addMissingDimensions(infoTable: SpreadLookupInfo) {
    this.addPivotIfEmpty(infoTable);
    this.addColumnIfEmpty(infoTable);
    this.addRowIfEmpty(infoTable);
    // Autofit empty result column
    setTimeout(() => spreadHelper.autoFitColumn(this.sheet, 1));
  }

  private addPivotIfEmpty(infoTable: SpreadLookupInfo) {
    if (infoTable.references.length === 3 && infoTable.thirdDimension === null) {
      const pivot = infoTable.references[2];
      const isPivotNumeric = pivot.match(/numeric/gi);
      const newPivotValue = isPivotNumeric ? '1' : 'A';

      infoTable.thirdDimension = newPivotValue;
      infoTable.toBeDeleted = false;

      infoTable.allThirdDimensions = this.spreadSheetData.filter(t => t.id === infoTable.id).map(x => x.thirdDimension);

      this.sheet.getCell(infoTable.startRow + 1, infoTable.startCol).value(newPivotValue);
      this.addNewThirdDimensionPrompt(infoTable);
    }
  }

  private addRowIfEmpty(infoTable: SpreadLookupInfo) {
    if (infoTable.rows.length === 0) {
      const dim1 = infoTable.references[0];
      const isRowNumeric = dim1.match(/numeric/gi);
      const newRowValue = isRowNumeric ? '1' : 'A';

      infoTable.rows.push(newRowValue);
      infoTable.numberOfRows = infoTable.rows.length || 1;
      infoTable.endRow += 1;

      this.sheet.getCell(infoTable.startRow + 2, infoTable.startCol).value(newRowValue);
      if (infoTable.results.length === 0) {
        infoTable.results.push([0]);
      }
    }
  }

  private addColumnIfEmpty(infoTable: SpreadLookupInfo) {
    if (infoTable.references.length > 1 && infoTable.cols.length === 0) {
      const dim2 = infoTable.references[1];
      const isColNumeric = dim2.match(/numeric/gi);
      const newColValue = isColNumeric ? '1' : 'A';

      infoTable.cols.push(newColValue);
      infoTable.numberOfColumns = infoTable.cols.length || 1;
      infoTable.endCol += 1;
      this.sheet.getCell(infoTable.startRow + 1, infoTable.startCol + 1).value(newColValue);
      if (infoTable.results.length === 0) {
        infoTable.results.push([0]);
      }
    }
  }

  private addNewThirdDimensionPrompt(infoTable: SpreadLookupInfo) {
    const isBalanceAssumption = infoTable.category.toLowerCase() === 'balancesheet';
    const isCommitDraw = infoTable.category.toLowerCase() === 'commitdraw';

    if (infoTable.references.length === 3 && !isBalanceAssumption && !isCommitDraw && !this.selectedScenarios.length) {
      const newThirdDimensionCell = this.sheet.getCell(infoTable.endRow + 4, 0);
      newThirdDimensionCell.locked(false || !this.userRoleCanEdit);
      spreadHelper.setWatermark(newThirdDimensionCell, `Enter ${infoTable.dimMnemonics[2]}`);
      spreadHelper.setNewThirdDimensionStyle(newThirdDimensionCell);
      this.sheet.addRows(this.sheet.getRowCount(), 2);
    }
    // Autofit first empty result column
    setTimeout(() => spreadHelper.autoFitColumn(this.sheet, 1));
  }

  lookupTableSetupHasChanged() {
    // Only runs if there's been modifications, to avoid polling unnecessarily
    if (this.modifiedLookupTablesIds.length > 0) {
      this.tableSetupService
        .getLookupTableSetup()
        .toPromise()
        .then(
          data => {
            this.structureChanged = this.modifiedLookupTablesIds.find(x => !data.find(element => x.id === element.Id));
          },
          err => {}
        );
    }
  }

  singleTableButtonHidden() {
    return (
      !this.selectedTreeItem.value ||
      this.saving ||
      this.isLoading ||
      this.numberTrackedItems === 0 ||
      !this.isTableModified(this.selectedTreeItem.value)
    );
  }

  allTablesButtonHidden() {
    return (
      this.selectedTreeItem.name !== 'ALL' ||
      this.numberTrackedItems === 0 ||
      this.modifiedLookupTablesIds.length === 0 ||
      this.isLoading ||
      this.saving
    );
  }

  public saveAllButtonTooltip(): string {
    return this.numberTrackedItems === 1
      ? `Save 1 change (${this.getUnsavedTablesNames()})`
      : `Save changes to ${this.numberTrackedItems} tables: ${this.getUnsavedTablesNames()}`;
  }

  public discardAllButtonTooltip(): string {
    return this.numberTrackedItems === 1
      ? `Discard 1 change (${this.getUnsavedTablesNames()})`
      : `Discard changes to ${this.numberTrackedItems} tables: ${this.getUnsavedTablesNames()}`;
  }

  public discardButtonTooltip(): string {
    return `Discard changes to table: ${this.selectedTreeItem.name} (${this.selectedTreeItem.value})`;
  }

  public saveButtonTooltip(): string {
    return `Save changes to table: ${this.selectedTreeItem.name} (${this.selectedTreeItem.value})`;
  }

  getNextOrderingIndex(tableData: LookupData[], dimOrder: string): number {
    return (
      tableData.reduce((element, maxElement) => (element[dimOrder] > maxElement[dimOrder] ? element[dimOrder] : maxElement[dimOrder]), -1) +
      1
    );
  }

  public sidenavToggle() {
    this.sidenavVisible = !this.sidenavVisible;
    this.menu.emitResize();
  }

  private debounceFunction(func, wait, immediate = false): void {
    const later = () => {
      this.timeOut = null;
      if (!immediate) {
        func();
      }
    };

    clearTimeout(this.timeOut);
    this.timeOut = setTimeout(later, wait);
    if (immediate && !this.timeOut) {
      func();
    }
  }
}
